import { Buffer } from 'buffer';
import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';

import { Box, Button } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import LangEnum from '../../../../lang/LangEnum';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { validateOrderConditionsQuery } from '../../contexts/order.utils';
import { useSignatureContext } from '../contexts/signature.context';
import useCanvasStyle from './useCanvasStyle';

interface DrawTabPanelProps {
  orderId: string;
}

const DrawTabPanel = ({ orderId }: DrawTabPanelProps) => {
  const {
    changeHandleGenerateSignedContractPdf,
    changeCleanSign,
    changeHasSignInput,
    hasSignInput,
  } = useSignatureContext();
  const { translate } = useTranslation();
  const refCanvas: MutableRefObject<HTMLCanvasElement | null> = useRef(null);
  const refSignaturePad: MutableRefObject<SignaturePad | null> = useRef(null);
  const { canvasClasses, screenHeight, screenWidth } = useCanvasStyle();
  const { team } = useApplicationContext();
  const queryClient = useQueryClient();

  const refetchValidateConditionsQuery = useCallback(() => {
    queryClient.refetchQueries(validateOrderConditionsQuery(orderId, team?.id));
  }, [queryClient, orderId, team?.id]);

  const getSign = useCallback((): Buffer => {
    const canvas = refCanvas.current!;
    const data = canvas.toDataURL().replace(/^data:image\/\w+;base64,/, '');
    return Buffer.from(data, 'base64');
  }, []);

  const handleClear = () => {
    changeHasSignInput(false);
    refSignaturePad.current!.clear();
  };

  const handleChange = useCallback(() => {
    changeHasSignInput(true);
  }, [changeHasSignInput]);

  useEffect(() => {
    const canvas = refCanvas.current!;
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    refSignaturePad.current = new SignaturePad(canvas, {
      backgroundColor: 'rgba(255, 255, 255, 0)',
      penColor: 'rgb(0, 0, 0)',
    });

    refSignaturePad.current!.addEventListener('beginStroke', () => {
      handleChange();
    });
  }, [handleChange]);

  useEffect(() => {
    changeHandleGenerateSignedContractPdf(getSign, refetchValidateConditionsQuery);
    changeCleanSign(() => refSignaturePad.current!.clear());
  }, [
    getSign,
    changeCleanSign,
    refetchValidateConditionsQuery,
    changeHandleGenerateSignedContractPdf,
  ]);

  return (
    <>
      <Box
        sx={{
          pt: 4,
        }}
      >
        <canvas
          id="signature-pad"
          className={`signature-pad ${canvasClasses.canvas}`}
          width={screenWidth}
          height={screenHeight}
          ref={refCanvas}
        />
      </Box>
      <Box
        sx={{
          pt: 4,
          width: '100%',
          justifyContent: 'center',
          display: 'flex',
        }}
      >
        <Box
          sx={{
            minWidth: 100,
          }}
        >
          <Button
            variant={hasSignInput ? 'contained' : 'text'}
            color={hasSignInput ? 'primary' : 'inherit'}
            onClick={handleClear}
            fullWidth
          >
            {translate(LangEnum.GLOBAL_CLEAN)}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default DrawTabPanel;
